import { Fragment, ReactElement, useEffect } from "react";
import { Box, SxProps, Theme, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DetailedRound } from "../models/round";
import { CouponShare } from "../models/couponshares";
import { PoolXNumberFormat } from "../common/CurrencyFormatter";
import TheGameScoreCard from "../roundview/components/thegame/thegamescorecard";
import TheGameLiveMarket from "../roundview/components/thegame/thegamelivemarket";
import TheGamePrizePlan from "../roundview/components/thegame/thegameprizeplan";
import { TheGameManifest, toManifestSign } from "../roundview/components/thegame/thegamemanifest";
import {
  bestRowNow,
  cardSx,
  CORRECT,
  getMarketStates,
  LINE,
  mainSx,
  MarketResult,
  pageSx,
  ProgressCells,
  sectionTitleSx,
  supportiveSx,
  WRONG,
} from "../roundview/components/thegame/thegamecommon";
import { track } from "../analytics/analytics";

const cellSxByResult: Record<MarketResult, SxProps<Theme>> = {
  open: {},
  won: { background: CORRECT },
  hit: { background: CORRECT, opacity: 0.45 },
  miss: { background: WRONG },
  lost: { background: WRONG },
};

function LegendItem({ swatch, label }: { swatch: SxProps<Theme>; label: string }): ReactElement {
  return (
    <Box component="span" sx={{ display: "flex", alignItems: "center", gap: "6px", fontSize: 13, color: "var(--font-color-supportive)" }}>
      <Box component="span" sx={{ width: 14, height: 14, borderRadius: "3px", ...(swatch as object) } as SxProps<Theme>} />
      {label}
    </Box>
  );
}

interface TheGameLiveCouponProps {
  round: DetailedRound;
  manifest: TheGameManifest;
  couponShare: CouponShare;
}

export default function TheGameLiveCoupon({ round, manifest, couponShare }: TheGameLiveCouponProps): ReactElement {
  const { t } = useTranslation();
  const roundId = round.id;
  useEffect(() => {
    track("live_coupon_viewed", { round_id: roundId });
  }, [roundId]);
  const bets = couponShare.coupon.coupon?.bets ?? [];
  const states = getMarketStates(round, manifest, bets);
  const numLegs = states.length;
  const best = bestRowNow(round, bets);

  const stake = couponShare.stake;
  const nf = PoolXNumberFormat(t("locale"), { style: "currency", currency: stake.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const stillNeeded = manifest.markets.filter((m) => states[m.pos]?.result === "miss");
  const target = numLegs - states.filter((s) => s.result === "lost").length;
  const showStillNeeded = round.status === "ACTIVE" && stillNeeded.length > 0;

  const neededText = (pos: number): string => {
    const market = manifest.markets.find((m) => m.pos === pos)!;
    const mine = states[pos].mine;
    const sign = mine.length === 1 ? toManifestSign(mine[0]) : undefined;
    const selection = sign ? market.selections[sign] : undefined;
    return selection ? `${market.label} – ${selection}` : market.label;
  };

  return (
    <Box sx={pageSx}>
      <Box component="main" sx={mainSx}>
        <TheGameScoreCard round={round} manifest={manifest} states={states} />

        <Box component="section" sx={{ ...cardSx, padding: "16px", display: "flex", flexDirection: "column", gap: "12px" } as SxProps<Theme>}>
          <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "8px" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography sx={{ fontFamily: "var(--font-title)", fontSize: 16, fontWeight: 700, textTransform: "uppercase" }}>
                {t("my_coupon")}
              </Typography>
              <Typography sx={{ ...supportiveSx, fontSize: 14 } as SxProps<Theme>}>
                {couponShare.coupon.totalRows} {t("rows")} · {nf.format(Number(stake.amount))}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
              <Typography sx={supportiveSx}>{t("best_row_now")}</Typography>
              <Typography sx={{ fontFamily: "var(--font-body)", fontSize: 32, fontWeight: 700, lineHeight: "34px", color: CORRECT }}>
                {best}/{numLegs}
              </Typography>
            </Box>
          </Box>
          <ProgressCells cells={states.map((s) => cellSxByResult[s.result])} />
          {showStillNeeded && (
            <Typography sx={{ fontSize: 14, lineHeight: "20px", color: "var(--font-color-supportive)" }}>
              {t("you_still_need", { target: `${target}/${numLegs}`, interpolation: { escapeValue: false } })}{" "}
              {stillNeeded.map((m, i) => (
                <Fragment key={m.pos}>
                  {i > 0 && (i === stillNeeded.length - 1 ? ` ${t("and")} ` : ", ")}
                  <Box component="strong" sx={{ color: "var(--text-black)" }}>{neededText(m.pos)}</Box>
                </Fragment>
              ))}
              .
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", paddingTop: "4px" }}>
          <Typography component="h2" sx={sectionTitleSx}>{t("markets")}</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px 14px" }}>
            <LegendItem swatch={{ background: "var(--sign-background)" }} label={t("your_pick")} />
            <LegendItem swatch={{ boxShadow: `inset 0 0 0 2px ${CORRECT}` }} label={t("correct_right_now")} />
            <LegendItem swatch={{ background: CORRECT }} label={t("tg_settled")} />
            <LegendItem swatch={{ background: "var(--thegame-pill-background, var(--sign-background-not-selected))", boxShadow: `inset 0 0 0 1px ${LINE}` }} label={t("cant_happen")} />
          </Box>
        </Box>

        {manifest.markets.map((market) =>
          states[market.pos] ? <TheGameLiveMarket key={market.pos} market={market} state={states[market.pos]} showResult /> : null
        )}

        <TheGamePrizePlan round={round} numLegs={numLegs} best={best} />
      </Box>
    </Box>
  );
}
